import { Box } from '@mui/material';
import { useCallback, useEffect } from 'react';
import { useSearchParams } from 'react-router';

import { PropertyTicketScopeChip } from '~/components/portfolio/PropertyTicketScopeChip';
import { TicketTable } from '~/components/tickets/TicketTable';
import { useDetailTableHeight } from '~/hooks/useDetailTableHeight';
import { useTicketsForTable, useTicketsTableModel } from '~/hooks/useTicketsForTable';

type PropertyTicketsProps = {
  propertyId: string;
};

export function PropertyTickets({ propertyId }: PropertyTicketsProps) {
  const tableHeight = useDetailTableHeight();
  const [searchParams, setSearchParams] = useSearchParams();

  const {
    sortModel,
    setSortModel,
    paginationModel,
    setPaginationModel,
    filterModel,
    setFilterModel,
    clearUrlFilters,
  } = useTicketsTableModel();

  const { tickets, numTickets, isLoading, onExport } = useTicketsForTable({
    paginationModel,
    sortModel,
    filterModel,
    propertyId,
  });

  // Extract the entry type from the search params and set the filter model
  // and remove it from the search params
  const entryType = searchParams.get('entry_type');
  useEffect(() => {
    if (entryType) {
      setFilterModel({
        items: [{ id: 'entryType', field: 'entryType', value: [entryType], operator: 'isAnyOf' }],
      });
      setSearchParams((searchParams) => {
        const nextSearchParams = new URLSearchParams(searchParams);
        nextSearchParams.delete('entry_type');
        return nextSearchParams;
      });
    }
  }, [entryType, setFilterModel, setSearchParams]);

  // The elevator the KPI link named. It narrows the fetch rather than the grid, so the chip
  // is the only thing that can show it, and dismissing it is the only thing that can undo it
  // short of Clear Filters taking the whole lot.
  const carId = searchParams.get('car_id');
  const clearCarScope = useCallback(
    () =>
      setSearchParams(
        (params) => {
          params.delete('car_id');
          return params;
        },
        { replace: true },
      ),
    [setSearchParams],
  );

  return (
    <Box display="flex" flexDirection="column" gap={2} width="100%">
      <TicketTable
        previewList="property_tickets"
        height={tableHeight}
        tickets={tickets}
        numTickets={numTickets}
        isLoading={isLoading}
        onExport={onExport}
        onPaginationModelChange={setPaginationModel}
        onSortModelChange={setSortModel}
        onFilterModelChange={setFilterModel}
        paginationModel={paginationModel}
        filterModel={filterModel}
        sortModel={sortModel}
        onClearUrlFilters={clearUrlFilters}
        scopeContent={
          carId ? <PropertyTicketScopeChip carId={carId} onClear={clearCarScope} /> : undefined
        }
        hiddenColumns={['property', 'portfolio']}
        viewsStoreKey="tickets-table-property"
        enableQuickFilter
        canFilterProperty={false}
      />
    </Box>
  );
}
